import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import AddTransactionModal from '../components/AddTransactionModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: string;
  category: { name: string };
}

export default function Transactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  async function fetchTransactions() {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*, category:category_id(name)')
        .order('date', { ascending: false });
      if (error) throw error;
      setTransactions(data || []);
    } catch {
      console.error('Error fetching transactions');
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Transactions</h1>
        <Button className="gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold btn-hover-lift" onClick={() => setShowAddModal(true)}>
          <Plus className="w-5 h-5" />
          Add Transaction
        </Button>
      </div>

      <AddTransactionModal open={showAddModal} onClose={() => setShowAddModal(false)} onSuccess={fetchTransactions} />

      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-slate-900">Transaction History</h2>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell className="text-slate-900">{new Date(transaction.date).toLocaleDateString()}</TableCell>
                  <TableCell className="text-slate-900">{transaction.description}</TableCell>
                  <TableCell className="text-slate-600">{transaction.category?.name}</TableCell>
                  <TableCell className="capitalize text-slate-900">{transaction.type}</TableCell>
                  <TableCell className={`text-right font-medium ${transaction.type === 'expense' ? 'text-red-600' : 'text-slate-700'}`}>
                    {Math.abs(transaction.amount).toLocaleString('en-US', { style: 'currency', minimumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
